import { AttributionControl, Map as MapLibreMap, NavigationControl, ScaleControl } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { initMapLibre } from '@/map/maplibre';
import { PRAGUE_BOUNDS, PRAHA6_BOUNDS, buildBaseStyle } from '@/map/style';

interface MapViewProps {
  /** Called once the style has loaded, so overlays can attach their layers. */
  onReady?: (map: MapLibreMap) => void;
}

export function MapView({ onReady }: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    initMapLibre();

    const map = new MapLibreMap({
      container: containerRef.current,
      style: buildBaseStyle(),
      // The map opens on MČ Praha 6, not the whole city — but the basemap
      // extract, the data and maxBounds below stay city-wide, so the district
      // is a starting *camera*, not a crop. `bounds` + `fitBoundsOptions`
      // (rather than a fixed center/zoom) makes the district fill the
      // viewport at any window size instead of over/under-shooting on very
      // wide or very narrow screens. PRAHA6_BOUNDS sits well inside
      // PRAGUE_BOUNDS (and further still inside maxBounds below), so
      // MapLibre's initial-camera clamp to maxBounds never kicks in here.
      bounds: PRAHA6_BOUNDS,
      fitBoundsOptions: { padding: 24 },
      minZoom: 9,
      maxZoom: 18,
      maxBounds: [
        [PRAGUE_BOUNDS[0] - 0.15, PRAGUE_BOUNDS[1] - 0.1],
        [PRAGUE_BOUNDS[2] + 0.15, PRAGUE_BOUNDS[3] + 0.1],
      ],
      // Attribution is a licence condition of the OSM-derived basemap, so it is
      // rendered as a permanently expanded control, never a collapsed "i".
      attributionControl: false,
      // Respect the OS setting rather than animating camera moves regardless.
      fadeDuration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300,
    });
    mapRef.current = map;

    map.addControl(new AttributionControl({ compact: false }), 'bottom-right');
    map.addControl(new NavigationControl({ visualizePitch: false }), 'top-right');
    map.addControl(new ScaleControl({ maxWidth: 100, unit: 'metric' }), 'bottom-left');
    map.getCanvas().setAttribute('aria-label', 'Mapa Prahy 6 s označenými problémy');

    map.on('error', (e) => {
      // A missing glyph range is cosmetic in GL JS >= 5.11 (it renders the
      // codepoint locally); anything else is worth surfacing.
      const message = e.error?.message ?? 'Neznámá chyba mapy';
      if (/glyph/i.test(message)) return;
      console.error('[map]', message);
      setError(message);
    });

    map.once('load', () => onReady?.(map));

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [onReady]);

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" data-testid="map-container" />
      {error && (
        <div
          role="alert"
          className="absolute left-1/2 top-4 z-10 -translate-x-1/2 rounded-md bg-white px-4 py-2 text-sm shadow-lg ring-1 ring-red-300"
        >
          Mapu se nepodařilo načíst: {error}
        </div>
      )}
    </div>
  );
}
